import React from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Target, Users, TrendingUp, CheckCircle2, Calendar, MessageCircle, GraduationCap, Award, Briefcase, Eye, Recycle, ArrowRight, Sparkles, BarChart3, Heart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
export function ImpactPage() {
  return <Layout data-id="element-1208">
      <SEO {...pageSEO.impact} data-id="element-1209" />
      {/* Header */}
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-1210">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1211">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1 text-sm font-medium text-blue-200 mb-4" data-id="element-1212">
            Une association naissante, des ambitions concrètes
          </span>
          <h1 className="text-4xl font-bold mb-4" data-id="element-1213">Notre Vision d'Impact</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-1214">
            L'ADCC construit son impact pas à pas, avec transparence et rigueur.
            Découvrez notre point de départ, nos objectifs 2026 et comment nous
            mesurerons notre réussite.
          </p>
        </div>
      </div>

      {/* Notre Point de Départ - Le Forum Fondateur */}
      <section className="py-20 bg-white" data-id="element-1215">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1216">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1217">
            <div data-id="element-1218">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1219">
                Notre Point de Départ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1220">
                Le Forum Fondateur
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1221">
                Nos premiers pas ont été marqués par un{' '}
                <strong data-id="element-1222">forum d'état des lieux</strong>, qui a réuni une dizaine
                de personnels de maison de différents domaines pour
                cartographier leurs besoins et aspirations.
              </p>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg mb-6" data-id="element-1223">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1224">
                  <MessageCircle className="w-5 h-5 text-amber-600" data-id="element-1225" />
                  Ce que nous avons appris
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-1226">
                  <li className="flex items-start gap-2" data-id="element-1227">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1228" />
                    <span data-id="element-1229">
                      Un besoin criant de formation professionnelle adaptée
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1230">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1231" />
                    <span data-id="element-1232">
                      Une demande forte de reconnaissance et de dignité
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1233">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1234" />
                    <span data-id="element-1235">
                      L'importance d'un accompagnement vers l'emploi structuré
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-600" data-id="element-1236">
                Ce forum a posé les bases de notre approche :{' '}
                <strong data-id="element-1237">écouter d'abord, agir ensuite</strong>. Chaque action de
                l'ADCC est construite à partir des besoins réels exprimés par
                les premiers concernés.
              </p>
            </div>

            <div className="relative" data-id="element-1238">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-1239">
                <img src="https://cdn.magicpatterns.com/uploads/exRoSvgKroC4rrK6KpbESt/WhatsApp_Image_2026-01-29_at_17.21.09.jpg" alt="Forum fondateur ADCC - Réunion avec les personnels de maison" className="w-full h-auto object-cover" data-id="element-1240" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-lg shadow-lg border border-gray-100 hidden md:block" data-id="element-1241">
                <div className="flex items-center gap-3" data-id="element-1242">
                  <div className="w-12 h-12 bg-[#1E88E5] rounded-full flex items-center justify-center" data-id="element-1243">
                    <Users className="w-6 h-6 text-white" data-id="element-1244" />
                  </div>
                  <div data-id="element-1245">
                    <p className="font-bold text-gray-900" data-id="element-1246">Forum Fondateur</p>
                    <p className="text-sm text-gray-600" data-id="element-1247">2024 • Bénin</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Objectifs 2026 */}
      <section className="py-20 bg-gray-50" data-id="element-1248">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1249">
          <div className="text-center mb-12" data-id="element-1250">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1251">
              Cap sur 2026
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1252">
              Nos Objectifs Chiffrés
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1253">
              Des ambitions réalistes et mesurables pour notre première année
              d'activité pleine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1254">
            <Card className="text-center border-t-4 border-t-[#1E88E5] bg-white" data-id="element-1255">
              <CardContent className="pt-8 pb-6" data-id="element-1256">
                <div className="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-4" data-id="element-1257">
                  <GraduationCap className="w-8 h-8 text-[#1E88E5]" data-id="element-1258" />
                </div>
                <p className="text-5xl font-bold text-[#1E88E5] mb-2" data-id="element-1259">100</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1260">
                  Personnes à former
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1261">
                  Via nos ateliers de Mars 2026 et sessions complémentaires
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-t-4 border-t-green-500 bg-white" data-id="element-1262">
              <CardContent className="pt-8 pb-6" data-id="element-1263">
                <div className="inline-flex items-center justify-center p-4 bg-green-50 rounded-full mb-4" data-id="element-1264">
                  <Briefcase className="w-8 h-8 text-green-600" data-id="element-1265" />
                </div>
                <p className="text-5xl font-bold text-green-600 mb-2" data-id="element-1266">60%</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1267">
                  Taux de placement visé
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1268">
                  Des bénéficiaires formés insérés professionnellement
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-t-4 border-t-purple-500 bg-white" data-id="element-1269">
              <CardContent className="pt-8 pb-6" data-id="element-1270">
                <div className="inline-flex items-center justify-center p-4 bg-purple-50 rounded-full mb-4" data-id="element-1271">
                  <Heart className="w-8 h-8 text-purple-600" data-id="element-1272" />
                </div>
                <p className="text-5xl font-bold text-purple-600 mb-2" data-id="element-1273">100%</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1274">
                  Placements éthiques
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1275">
                  Transparents et respectueux des droits
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-1276">
            <div className="flex flex-col md:flex-row items-center gap-6" data-id="element-1277">
              <div className="flex-shrink-0" data-id="element-1278">
                <div className="w-16 h-16 bg-[#1E88E5]/10 rounded-full flex items-center justify-center" data-id="element-1279">
                  <Target className="w-8 h-8 text-[#1E88E5]" data-id="element-1280" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left" data-id="element-1281">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1282">
                  Pourquoi ces objectifs ?
                </h3>
                <p className="text-gray-600" data-id="element-1283">
                  Ces chiffres sont ambitieux mais réalistes pour une première
                  année. Nous préférons des objectifs atteignables que nous
                  pourrons dépasser, plutôt que des promesses impossibles à
                  tenir. La crédibilité se construit dans la durée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Théorie du Changement */}
      <section className="py-20 bg-white" data-id="element-1284">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1285">
          <div className="text-center mb-12" data-id="element-1286">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1287">
              Notre Approche
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1288">
              La Chaîne de Valeur ADCC
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1289">
              Une approche complète, de la formation à l'emploi durable, pour un
              impact réel et mesurable.
            </p>
          </div>

          <div className="relative" data-id="element-1290">
            {/* Connection line - desktop only */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-green-200 to-purple-200 transform -translate-y-1/2 z-0" data-id="element-1291"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative z-10" data-id="element-1292">
              {[{
              icon: GraduationCap,
              title: 'Former',
              desc: 'Compétences techniques et humaines',
              color: 'bg-blue-50',
              iconColor: 'text-blue-600'
            }, {
              icon: Award,
              title: 'Certifier',
              desc: 'Validation et professionnalisation',
              color: 'bg-green-50',
              iconColor: 'text-green-600'
            }, {
              icon: Briefcase,
              title: 'Placer',
              desc: 'Mise en relation avec employeurs',
              color: 'bg-purple-50',
              iconColor: 'text-purple-600'
            }, {
              icon: Eye,
              title: 'Suivre',
              desc: 'Accompagnement post-placement',
              color: 'bg-amber-50',
              iconColor: 'text-amber-600'
            }, {
              icon: Recycle,
              title: 'Recycler',
              desc: 'Mise à jour des compétences',
              color: 'bg-teal-50',
              iconColor: 'text-teal-600'
            }].map((step, index) => <div key={index} className="flex flex-col items-center" data-id="element-1293">
                  <div className={`w-20 h-20 ${step.color} rounded-full flex items-center justify-center mb-4 shadow-sm border-4 border-white`} data-id="element-1294">
                    <step.icon className={`w-10 h-10 ${step.iconColor}`} data-id="element-1295" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1296">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 text-center" data-id="element-1297">
                    {step.desc}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-[#1E88E5] to-[#1976D2] rounded-2xl p-8 text-white text-center" data-id="element-1298">
            <Sparkles className="w-10 h-10 mx-auto mb-4 text-yellow-300" data-id="element-1299" />
            <p className="text-2xl font-bold mb-2" data-id="element-1300">
              "Former sans débouché n'est pas suffisant."
            </p>
            <p className="text-blue-100" data-id="element-1301">
              Notre engagement : transformer les compétences en opportunités
              d'emploi durables.
            </p>
          </div>
        </div>
      </section>

      {/* Comment nous mesurerons notre impact */}
      <section className="py-20 bg-gray-50" data-id="element-1302">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1303">
          <div className="text-center mb-12" data-id="element-1304">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1305">
              Transparence & Rigueur
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1306">
              Comment nous mesurerons notre impact
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1307">
              Des indicateurs clairs pour évaluer notre réussite et rendre
              compte à nos partenaires et bénéficiaires.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1308">
            {[{
            icon: Users,
            title: 'Nombre de bénéficiaires formés',
            desc: 'Comptabilisation des participants ayant complété nos programmes de formation',
            target: 'Objectif 2026 : 100 personnes'
          }, {
            icon: Briefcase,
            title: "Taux d'insertion professionnelle",
            desc: 'Pourcentage de bénéficiaires formés ayant trouvé un emploi dans les 6 mois',
            target: 'Objectif 2026 : 60%'
          }, {
            icon: TrendingUp,
            title: 'Durabilité des placements',
            desc: 'Suivi à 6 et 12 mois pour mesurer la stabilité des emplois obtenus',
            target: 'Suivi systématique prévu'
          }, {
            icon: BarChart3,
            title: 'Satisfaction des bénéficiaires',
            desc: 'Enquêtes régulières auprès des personnes formées et placées',
            target: 'Questionnaires post-formation et post-placement'
          }].map((metric, index) => <Card key={index} className="bg-white" data-id="element-1309">
                <CardContent className="p-6" data-id="element-1310">
                  <div className="flex items-start gap-4" data-id="element-1311">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1312">
                      <metric.icon className="w-6 h-6 text-[#1E88E5]" data-id="element-1313" />
                    </div>
                    <div className="flex-1" data-id="element-1314">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-1315">
                        {metric.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3" data-id="element-1316">
                        {metric.desc}
                      </p>
                      <span className="inline-block bg-blue-50 text-[#1E88E5] text-xs font-medium px-3 py-1 rounded-full" data-id="element-1317">
                        {metric.target}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 border border-gray-100 shadow-sm" data-id="element-1318">
            <div className="flex flex-col md:flex-row items-center gap-6" data-id="element-1319">
              <div className="flex-shrink-0" data-id="element-1320">
                <Calendar className="w-12 h-12 text-[#1E88E5]" data-id="element-1321" />
              </div>
              <div className="flex-1 text-center md:text-left" data-id="element-1322">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1323">
                  Rendez-vous fin 2026
                </h3>
                <p className="text-gray-600" data-id="element-1324">
                  Nous publierons notre premier rapport d'impact fin 2026, avec
                  des données réelles et vérifiables. D'ici là, suivez notre
                  actualité sur notre blog et nos réseaux sociaux pour des mises
                  à jour régulières sur nos avancées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - Rejoignez l'aventure */}
      <section className="py-20 bg-white" data-id="element-1325">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1326">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1327">
            Construisons cet impact ensemble
          </h2>
          <p className="text-xl text-gray-600 mb-10" data-id="element-1328">
            L'ADCC est une aventure collective. Que vous soyez un futur
            bénéficiaire, un employeur responsable ou un partenaire engagé, vous
            pouvez contribuer à notre vision d'impact.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-1329">
            <Link to="/actions" className="group" data-id="element-1330">
              <div className="bg-blue-50 rounded-xl p-6 h-full border-2 border-transparent hover:border-[#1E88E5] transition-all" data-id="element-1331">
                <GraduationCap className="w-10 h-10 text-[#1E88E5] mx-auto mb-4" data-id="element-1332" />
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-1333">Vous former</h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-1334">
                  Inscrivez-vous aux ateliers de Mars 2026
                </p>
                <span className="text-[#1E88E5] font-medium text-sm flex items-center justify-center gap-1 group-hover:gap-2 transition-all" data-id="element-1335">
                  S'inscrire <ArrowRight className="w-4 h-4" data-id="element-1336" />
                </span>
              </div>
            </Link>

            <Link to="/actions" className="group" data-id="element-1337">
              <div className="bg-green-50 rounded-xl p-6 h-full border-2 border-transparent hover:border-green-500 transition-all" data-id="element-1338">
                <Briefcase className="w-10 h-10 text-green-600 mx-auto mb-4" data-id="element-1339" />
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-1340">Recruter</h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-1341">
                  Devenez employeur partenaire
                </p>
                <span className="text-green-600 font-medium text-sm flex items-center justify-center gap-1 group-hover:gap-2 transition-all" data-id="element-1342">
                  En savoir plus <ArrowRight className="w-4 h-4" data-id="element-1343" />
                </span>
              </div>
            </Link>

            <Link to="/donate" className="group" data-id="element-1344">
              <div className="bg-purple-50 rounded-xl p-6 h-full border-2 border-transparent hover:border-purple-500 transition-all" data-id="element-1345">
                <Heart className="w-10 h-10 text-purple-600 mx-auto mb-4" data-id="element-1346" />
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-1347">Soutenir</h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-1348">
                  Contribuez financièrement à notre mission
                </p>
                <span className="text-purple-600 font-medium text-sm flex items-center justify-center gap-1 group-hover:gap-2 transition-all" data-id="element-1349">
                  Faire un don <ArrowRight className="w-4 h-4" data-id="element-1350" />
                </span>
              </div>
            </Link>
          </div>

          <Link to="/partner" data-id="element-1351">
            <Button size="lg" className="gap-2" data-id="element-1352">
              Proposer un partenariat
              <ArrowRight className="w-5 h-5" data-id="element-1353" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}